import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { StatCard } from "@/components/ui/stat-card";
import { StatusBadge } from "@/components/ui/status-badge";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { formatPersonName } from "@/lib/person-name";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeftRight,
  Search,
  BookOpen,
  RotateCcw,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  User,
  CreditCard,
  BookMarked,
  Ban,
  Loader2,
  ShieldAlert,
  ScanBarcode,
  BookX,
  ArrowDownToLine,
  CalendarClock,
  AlertCircle,
  Library,
} from "lucide-react";
import {
  circulationApi,
  type PatronLookupResult,
  type PatronSummary,
  type CheckoutRecord,
  type CheckInResult,
} from "@/lib/api/circulation";

/* ── Checkout status badge mapping ──────────────────────────── */

function CheckoutStatusBadge({ status, renewalCount }: { status: string; renewalCount: number }) {
  const map: Record<
    string,
    { variant: "success" | "warning" | "info" | "danger" | "neutral"; label: string }
  > = {
    ACTIVE: { variant: "info", label: "Active" },
    OVERDUE: { variant: "danger", label: "Overdue" },
    RETURNED: { variant: "success", label: "Returned" },
    LOST: { variant: "danger", label: "Lost" },
  };
  const cfg = map[status] ?? { variant: "neutral" as const, label: status };
  return (
    <StatusBadge variant={cfg.variant}>
      {cfg.label}
      {renewalCount > 0 && ` (R${renewalCount})`}
    </StatusBadge>
  );
}

/* ── Main component ─────────────────────────────────────────── */

const CirculationDeskPage = () => {
  const toast = useToast();
  const location = useLocation();

  // Patron state
  const [patronQuery, setPatronQuery] = useState("");
  const [patronResults, setPatronResults] = useState<PatronLookupResult[]>([]);
  const [patronLoading, setPatronLoading] = useState(false);
  const [openingPatron, setOpeningPatron] = useState(false);
  const [selectedPatron, setSelectedPatron] = useState<PatronSummary | null>(null);

  // Item state
  const [itemInput, setItemInput] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Check-in state
  const [checkInInput, setCheckInInput] = useState("");
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [lastCheckIn, setLastCheckIn] = useState<CheckInResult | null>(null);
  const [checkInHistory, setCheckInHistory] = useState<CheckInResult[]>([]);

  // Override dialog
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [pendingCheckout, setPendingCheckout] = useState<{ barcode: string } | null>(null);

  // Renew
  const [renewLoading, setRenewLoading] = useState(false);

  // In-house checkout (FR-10)
  const [inHouseMode, setInHouseMode] = useState(false);

  // Declare Lost dialog (FR-24)
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [lostCheckoutId, setLostCheckoutId] = useState<string | null>(null);
  const [lostReplacementCost, setLostReplacementCost] = useState("");
  const [lostNotes, setLostNotes] = useState("");
  const [lostLoading, setLostLoading] = useState(false);

  // Declare Damaged dialog (FR-25)
  const [damagedDialogOpen, setDamagedDialogOpen] = useState(false);
  const [damagedCheckoutId, setDamagedCheckoutId] = useState<string | null>(null);
  const [damagedFineAmount, setDamagedFineAmount] = useState("");
  const [damagedDescription, setDamagedDescription] = useState("");
  const [damagedNotes, setDamagedNotes] = useState("");
  const [damagedLoading, setDamagedLoading] = useState(false);

  // Tabs
  const [activeTab, setActiveTab] = useState<"checkout" | "checkin">("checkout");

  const patronInputRef = useRef<HTMLInputElement>(null);
  const itemInputRef = useRef<HTMLInputElement>(null);
  const checkInInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    patronInputRef.current?.focus();
  }, []);

  // Pre-fill from navigation state (e.g. from Patrons list "View" button)
  useEffect(() => {
    const patronLookup = (location.state as { patronLookup?: string })?.patronLookup;
    if (patronLookup?.trim()) {
      setPatronQuery(patronLookup);
      handlePatronSearch(patronLookup);
      window.history.replaceState({}, "", location.pathname);
    }
  }, [location.state]);

  // Patron lookup with debounce
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const searchGeneration = useRef(0);
  const handlePatronSearch = useCallback((query: string) => {
    setPatronQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const generation = ++searchGeneration.current;
    if (query.trim().length < 2) {
      setPatronResults([]);
      setPatronLoading(false);
      return;
    }
    setPatronLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await circulationApi.lookupPatron(query.trim());
        if (generation !== searchGeneration.current) return;
        setPatronResults(results);
      } catch {
        if (generation !== searchGeneration.current) return;
        setPatronResults([]);
      } finally {
        if (generation === searchGeneration.current) setPatronLoading(false);
      }
    }, 300);
  }, []);

  const selectPatron = async (userId: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchGeneration.current += 1;
    setOpeningPatron(true);
    setPatronLoading(false);
    setPatronResults([]);
    try {
      const summary = await circulationApi.getPatronSummary(userId);
      setSelectedPatron(summary);
      setPatronQuery("");
      setTimeout(() => itemInputRef.current?.focus(), 100);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to load patron");
    } finally {
      setOpeningPatron(false);
    }
  };

  const clearPatron = () => {
    setSelectedPatron(null);
    setPatronQuery("");
    setPatronResults([]);
    patronInputRef.current?.focus();
  };

  // Checkout
  const handleCheckout = async (overrideData?: { reason: string }) => {
    const rawItem = (overrideData && pendingCheckout ? pendingCheckout.barcode : itemInput).trim();
    if (!selectedPatron?.patron || !rawItem) return;
    setCheckoutLoading(true);
    try {
      const isAccession = /^\d+$/.test(rawItem) && rawItem.length <= 8;
      await circulationApi.checkout({
        ...(isAccession ? { accessionNumber: parseInt(rawItem, 10) } : { barcode: rawItem }),
        userId: selectedPatron.patron.id,
        inHouse: inHouseMode || undefined,
        override: overrideData,
      });
      toast.success(inHouseMode ? "In-house use recorded" : "Item checked out successfully");
      setItemInput("");
      if (overrideData) {
        setOverrideOpen(false);
        setOverrideReason("");
        setPendingCheckout(null);
      }
      const updated = await circulationApi.getPatronSummary(selectedPatron.patron.id);
      setSelectedPatron(updated);
      itemInputRef.current?.focus();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      const reasons = err?.response?.data?.reasons;
      if (reasons && Array.isArray(reasons)) {
        setPendingCheckout({ barcode: rawItem });
        setOverrideOpen(true);
      } else {
        toast.error(msg ?? "Checkout failed");
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleOverrideConfirm = async () => {
    if (!pendingCheckout || !overrideReason.trim() || checkoutLoading) return;
    setItemInput(pendingCheckout.barcode);
    await handleCheckout({ reason: overrideReason.trim() });
  };

  // Check-in
  const handleCheckIn = async () => {
    if (!checkInInput.trim()) return;
    setCheckInLoading(true);
    try {
      const isAccession = /^\d+$/.test(checkInInput.trim()) && checkInInput.trim().length <= 8;
      const result = await circulationApi.checkIn(
        isAccession
          ? { accessionNumber: parseInt(checkInInput.trim(), 10) }
          : { barcode: checkInInput.trim() }
      );
      setLastCheckIn(result);
      setCheckInHistory((prev) => [result, ...prev].slice(0, 20));
      toast.success("Item checked in successfully");
      if (result.holdFulfilled) {
        toast.success("Hold fulfilled — item placed on hold shelf");
      }
      if (result.fineCreated) {
        toast.error(`Overdue fine created: ₦${parseFloat(result.fineCreated.amount).toFixed(2)}`);
      }
      setCheckInInput("");
      if (selectedPatron?.patron && result.checkout?.userId === selectedPatron.patron.id) {
        const updated = await circulationApi.getPatronSummary(selectedPatron.patron.id);
        setSelectedPatron(updated);
      }
      checkInInputRef.current?.focus();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Check-in failed");
    } finally {
      setCheckInLoading(false);
    }
  };

  // Renew
  const handleRenew = async (checkoutId: string) => {
    setRenewLoading(true);
    try {
      await circulationApi.renew(checkoutId);
      toast.success("Renewal successful");
      if (selectedPatron?.patron) {
        const updated = await circulationApi.getPatronSummary(selectedPatron.patron.id);
        setSelectedPatron(updated);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Renewal failed");
    } finally {
      setRenewLoading(false);
    }
  };

  // Declare Lost (FR-24)
  const openLostDialog = (checkoutId: string) => {
    setLostCheckoutId(checkoutId);
    setLostReplacementCost("");
    setLostNotes("");
    setLostDialogOpen(true);
  };

  const handleDeclareLost = async () => {
    if (!lostCheckoutId) return;
    setLostLoading(true);
    try {
      await circulationApi.declareLost(lostCheckoutId, {
        replacementCost: lostReplacementCost ? parseFloat(lostReplacementCost) : undefined,
        notes: lostNotes || undefined,
      });
      toast.success("Item declared lost");
      setLostDialogOpen(false);
      if (selectedPatron?.patron) {
        const updated = await circulationApi.getPatronSummary(selectedPatron.patron.id);
        setSelectedPatron(updated);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to declare lost");
    } finally {
      setLostLoading(false);
    }
  };

  // Declare Damaged (FR-25)
  const openDamagedDialog = (checkoutId: string) => {
    setDamagedCheckoutId(checkoutId);
    setDamagedFineAmount("");
    setDamagedDescription("");
    setDamagedNotes("");
    setDamagedDialogOpen(true);
  };

  const handleDeclareDamaged = async () => {
    if (!damagedCheckoutId || !damagedFineAmount || !damagedDescription.trim()) return;
    setDamagedLoading(true);
    try {
      await circulationApi.declareDamaged(damagedCheckoutId, {
        fineAmount: parseFloat(damagedFineAmount),
        damageDescription: damagedDescription.trim(),
        notes: damagedNotes || undefined,
      });
      toast.success("Item declared damaged — fine assessed");
      setDamagedDialogOpen(false);
      if (selectedPatron?.patron) {
        const updated = await circulationApi.getPatronSummary(selectedPatron.patron.id);
        setSelectedPatron(updated);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to declare damaged");
    } finally {
      setDamagedLoading(false);
    }
  };

  /* ── Checkout table columns ────────────────────────────────── */

  const checkoutColumns = useMemo<DataTableColumn<CheckoutRecord>[]>(
    () => [
      {
        id: "title",
        header: "Item",
        cell: (co) => (
          <div className="min-w-0">
            <p className="font-medium text-sm text-slate-900 truncate">
              {co.item?.biblio?.title ?? co.book?.title ?? "Unknown"}
            </p>
            <p className="text-xs text-slate-500 truncate mt-0.5">
              {[
                co.item?.barcode && `Barcode: ${co.item.barcode}`,
                co.item?.accessionNumber && `Acc#: ${co.item.accessionNumber}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        ),
      },
      {
        id: "dueDate",
        header: "Due Date",
        hideOnMobile: true,
        cell: (co) => (
          <span
            className={`text-sm tabular-nums ${
              co.status === "OVERDUE" ? "text-red-600 font-semibold" : "text-slate-600"
            }`}
          >
            {new Date(co.dueDate).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        align: "center",
        cell: (co) => <CheckoutStatusBadge status={co.status} renewalCount={co.renewalCount} />,
      },
      {
        id: "actions",
        header: "Actions",
        align: "right",
        cell: (co) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => handleRenew(co.id)}
              disabled={renewLoading}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Renew
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
              onClick={() => openLostDialog(co.id)}
              title="Declare Lost"
            >
              <Ban className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50 hover:border-amber-200"
              onClick={() => openDamagedDialog(co.id)}
              title="Declare Damaged"
            >
              <ShieldAlert className="w-3.5 h-3.5" />
            </Button>
          </div>
        ),
      },
    ],
    [renewLoading]
  );

  /* ── Check-in history columns ──────────────────────────────── */

  const checkInHistoryColumns = useMemo<DataTableColumn<CheckInResult>[]>(
    () => [
      {
        id: "title",
        header: "Item",
        cell: (r) => (
          <div className="min-w-0">
            <p className="font-medium text-sm text-slate-900 truncate">
              {r.checkout?.item?.biblio?.title ?? "Unknown Title"}
            </p>
            <p className="text-xs text-slate-500 truncate mt-0.5">
              {r.checkout?.item?.barcode && `Barcode: ${r.checkout.item.barcode}`}
            </p>
          </div>
        ),
      },
      {
        id: "patron",
        header: "Returned By",
        hideOnMobile: true,
        cell: (r) => (
          <span className="text-sm text-slate-600">
            {r.checkout?.user?.firstName} {r.checkout?.user?.lastName}
          </span>
        ),
      },
      {
        id: "notices",
        header: "Notices",
        align: "right",
        cell: (r) => (
          <div className="flex items-center justify-end gap-2">
            {r.fineCreated && (
              <StatusBadge variant="warning">
                Fine: ₦{parseFloat(r.fineCreated.amount).toFixed(2)}
              </StatusBadge>
            )}
            {r.holdFulfilled && <StatusBadge variant="info">Hold Fulfilled</StatusBadge>}
            {!r.fineCreated && !r.holdFulfilled && <StatusBadge variant="success">OK</StatusBadge>}
          </div>
        ),
      },
    ],
    []
  );

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Circulation Desk"
        description="Check out, check in, and renew library items"
        showSearch={false}
      />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "checkout" | "checkin")}>
        <TabsList className="mb-6 bg-slate-100/80 p-1">
          <TabsTrigger
            value="checkout"
            className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <BookOpen className="w-4 h-4" /> Check Out
          </TabsTrigger>
          <TabsTrigger
            value="checkin"
            className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <RotateCcw className="w-4 h-4" /> Check In
          </TabsTrigger>
        </TabsList>

        {/* ═══════════════════════════════════════════════════════ */}
        {/*  CHECK OUT TAB                                         */}
        {/* ═══════════════════════════════════════════════════════ */}
        <TabsContent value="checkout" className="space-y-6 mt-0">
          {!selectedPatron ? (
            /* ── Step 1: Find Patron ──────────────────────── */
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-3 mb-1">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                  <User className="w-4.5 h-4.5 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-slate-900">Step 1: Find Patron</h2>
                  <p className="text-sm text-slate-500">
                    Scan library card or search by name, email, or card number
                  </p>
                </div>
              </div>

              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  ref={patronInputRef}
                  value={patronQuery}
                  onChange={(e) => handlePatronSearch(e.target.value)}
                  placeholder="Name as shown in Patrons, email, or card number..."
                  className="pl-10 bg-white"
                  disabled={openingPatron}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && patronResults.length === 1) {
                      selectPatron(patronResults[0].id);
                    }
                  }}
                />
              </div>

              {(openingPatron || patronLoading) && (
                <div className="flex items-center justify-center py-6">
                  <LoadingSpinner
                    label={openingPatron ? "Opening patron…" : "Searching patrons…"}
                  />
                </div>
              )}

              {!openingPatron && patronResults.length > 0 && (
                <div className="mt-3 rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-auto">
                  {patronResults.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => selectPatron(p.id)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-3"
                    >
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary shrink-0">
                        {(p.firstName?.[0] ?? "").toUpperCase()}
                        {(p.lastName?.[0] ?? "").toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-900 truncate">
                          {formatPersonName(p.firstName, p.lastName)}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{p.email}</p>
                      </div>
                      {p.patronType && (
                        <Badge variant="secondary" className="text-xs shrink-0 shadow-none">
                          {p.patronType.name}
                        </Badge>
                      )}
                      {p.cardNumber && (
                        <span className="text-xs text-slate-400 font-mono shrink-0">
                          {p.cardNumber}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {!patronLoading &&
                !openingPatron &&
                patronResults.length === 0 &&
                patronQuery.trim().length >= 2 && (
                  <div className="mt-4">
                    <EmptyState
                      icon={<User className="w-6 h-6 text-slate-400" />}
                      title="No patrons found"
                      description="Try a different name, email, or card number"
                      compact
                    />
                  </div>
                )}
            </div>
          ) : (
            <>
              {/* ── Patron info card ──────────────────────── */}
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary shrink-0">
                      {(selectedPatron.patron?.firstName?.[0] ?? "").toUpperCase()}
                      {(selectedPatron.patron?.lastName?.[0] ?? "").toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-semibold text-slate-900">
                          {formatPersonName(
                            selectedPatron.patron?.firstName,
                            selectedPatron.patron?.lastName
                          )}
                        </h3>
                        {selectedPatron.patron?.patronType && (
                          <Badge variant="secondary" className="text-xs shadow-none">
                            {selectedPatron.patron.patronType.name}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 truncate">
                        {selectedPatron.patron?.email}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearPatron}
                    className="shrink-0 self-start sm:self-auto"
                  >
                    Change Patron
                  </Button>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                  <StatCard
                    icon={<BookOpen className="w-5 h-5" />}
                    label="Checked Out"
                    value={selectedPatron.stats.activeCheckouts}
                    iconBg="bg-blue-50 text-blue-600"
                  />
                  <StatCard
                    icon={<AlertCircle className="w-5 h-5" />}
                    label="Overdue"
                    value={selectedPatron.stats.overdueItems}
                    iconBg={
                      selectedPatron.stats.overdueItems > 0
                        ? "bg-red-50 text-red-600"
                        : "bg-slate-100 text-slate-400"
                    }
                  />
                  <StatCard
                    icon={<Clock className="w-5 h-5" />}
                    label="Holds"
                    value={selectedPatron.stats.pendingHolds + selectedPatron.stats.readyHolds}
                    iconBg="bg-purple-50 text-purple-600"
                  />
                  <StatCard
                    icon={<CreditCard className="w-5 h-5" />}
                    label="Unpaid Fines"
                    value={
                      selectedPatron.totalUnpaidFines > 0
                        ? `₦${selectedPatron.totalUnpaidFines.toFixed(2)}`
                        : "₦0.00"
                    }
                    iconBg={
                      selectedPatron.totalUnpaidFines > 0
                        ? "bg-amber-50 text-amber-600"
                        : "bg-slate-100 text-slate-400"
                    }
                  />
                </div>

                {/* Blocks warning */}
                {selectedPatron.blocks.blocked && (
                  <div className="mt-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                    <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-700">Checkout Blocked</p>
                      <ul className="text-sm text-red-600 mt-1 space-y-0.5">
                        {selectedPatron.blocks.reasons.map((r, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span className="mt-1.5 h-1 w-1 rounded-full bg-red-400 shrink-0" />
                            {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Step 2: Scan item ─────────────────────── */}
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                    <ScanBarcode className="w-4.5 h-4.5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-heading font-semibold text-slate-900">
                      Step 2: Scan or Enter Item
                    </h2>
                    <p className="text-sm text-slate-500">
                      Scan item barcode or type barcode / accession number
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Input
                    ref={itemInputRef}
                    value={itemInput}
                    onChange={(e) => setItemInput(e.target.value)}
                    placeholder="Scan or enter barcode / accession number..."
                    className="bg-white"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCheckout();
                    }}
                    disabled={checkoutLoading}
                  />
                  <Button
                    onClick={() => handleCheckout()}
                    disabled={checkoutLoading || !itemInput.trim()}
                    className="shrink-0"
                  >
                    {checkoutLoading ? (
                      <LoadingSpinner />
                    ) : inHouseMode ? (
                      <>
                        <Library className="w-4 h-4 mr-1.5" />
                        Record Use
                      </>
                    ) : (
                      <>
                        <ArrowDownToLine className="w-4 h-4 mr-1.5" />
                        Check Out
                      </>
                    )}
                  </Button>
                </div>

                <div className="flex items-center gap-2 mt-3">
                  <Checkbox
                    id="inHouseToggle"
                    checked={inHouseMode}
                    onCheckedChange={(v) => setInHouseMode(!!v)}
                  />
                  <Label
                    htmlFor="inHouseToggle"
                    className="text-sm text-slate-600 cursor-pointer select-none"
                  >
                    In-house / on-site use (due back same day)
                  </Label>
                </div>
              </div>

              {/* ── Current checkouts ─────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                    <BookMarked className="w-4.5 h-4.5 text-slate-400" />
                    Current Checkouts
                    {selectedPatron.checkouts.length > 0 && (
                      <span className="ml-1 text-sm font-normal text-slate-500">
                        ({selectedPatron.checkouts.length})
                      </span>
                    )}
                  </h2>
                </div>

                <DataTable
                  columns={checkoutColumns}
                  data={selectedPatron.checkouts}
                  rowKey={(co) => co.id}
                  showSerialNumber
                  emptyState={
                    <EmptyState
                      icon={<BookX className="w-6 h-6 text-slate-400" />}
                      title="No active checkouts"
                      description="This patron has no items currently checked out"
                      compact
                    />
                  }
                  mobileCard={(co) => (
                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-medium text-sm text-slate-900 line-clamp-2">
                          {co.item?.biblio?.title ?? co.book?.title ?? "Unknown"}
                        </p>
                        <CheckoutStatusBadge status={co.status} renewalCount={co.renewalCount} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                        {co.item?.barcode && <span>Barcode: {co.item.barcode}</span>}
                        <span className="flex items-center gap-1">
                          <CalendarClock className="w-3 h-3" />
                          Due: {new Date(co.dueDate).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 border-t border-slate-100 pt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs flex-1"
                          onClick={() => handleRenew(co.id)}
                          disabled={renewLoading}
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1" />
                          Renew
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:border-red-200"
                          onClick={() => openLostDialog(co.id)}
                          title="Declare Lost"
                        >
                          <Ban className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-50 hover:border-amber-200"
                          onClick={() => openDamagedDialog(co.id)}
                          title="Declare Damaged"
                        >
                          <ShieldAlert className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}
                />
              </div>
            </>
          )}
        </TabsContent>

        {/* ═══════════════════════════════════════════════════════ */}
        {/*  CHECK IN TAB                                          */}
        {/* ═══════════════════════════════════════════════════════ */}
        <TabsContent value="checkin" className="space-y-6 mt-0">
          {/* ── Scan item to check in ────────────────────── */}
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50">
                <RotateCcw className="w-4.5 h-4.5 text-emerald-600" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-slate-900">Check In Item</h2>
                <p className="text-sm text-slate-500">
                  Scan item barcode or enter barcode / accession number
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Input
                ref={checkInInputRef}
                value={checkInInput}
                onChange={(e) => setCheckInInput(e.target.value)}
                placeholder="Scan or enter barcode / accession number..."
                className="bg-white"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCheckIn();
                }}
                disabled={checkInLoading}
              />
              <Button
                onClick={handleCheckIn}
                disabled={checkInLoading || !checkInInput.trim()}
                className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {checkInLoading ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <ArrowDownToLine className="w-4 h-4 mr-1.5" />
                    Check In
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* ── Last check-in result ─────────────────────── */}
          {lastCheckIn && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-emerald-800 text-sm">
                    Last Check-In Successful
                  </h3>
                  <p className="font-medium text-sm text-slate-900 mt-1">
                    {lastCheckIn.checkout?.item?.biblio?.title ?? "Unknown Title"}
                  </p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Returned by: {lastCheckIn.checkout?.user?.firstName}{" "}
                    {lastCheckIn.checkout?.user?.lastName}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-3">
                    {lastCheckIn.fineCreated && (
                      <div className="flex items-center gap-1.5 text-sm text-slate-900 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1">
                        <CreditCard className="w-3.5 h-3.5" />
                        Overdue fine: ₦{parseFloat(lastCheckIn.fineCreated.amount).toFixed(2)}
                      </div>
                    )}
                    {lastCheckIn.holdFulfilled && (
                      <div className="flex items-center gap-1.5 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2.5 py-1">
                        <Clock className="w-3.5 h-3.5" />
                        Hold fulfilled for {lastCheckIn.holdFulfilled.user?.firstName}{" "}
                        {lastCheckIn.holdFulfilled.user?.lastName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Check-in session history ─────────────────── */}
          {checkInHistory.length > 0 && (
            <div>
              <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2 mb-3">
                <ArrowLeftRight className="w-4.5 h-4.5 text-slate-400" />
                Session History
                <span className="text-sm font-normal text-slate-500">
                  ({checkInHistory.length})
                </span>
              </h2>

              <DataTable
                columns={checkInHistoryColumns}
                data={checkInHistory}
                rowKey={(r) => r.checkout?.id ?? Math.random().toString(36)}
                showSerialNumber
                emptyState={null}
                mobileCard={(r) => (
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <p className="font-medium text-sm text-slate-900 truncate mb-1">
                      {r.checkout?.item?.biblio?.title ?? "Unknown Title"}
                    </p>
                    <p className="text-xs text-slate-500 mb-2">
                      Returned by: {r.checkout?.user?.firstName} {r.checkout?.user?.lastName}
                    </p>
                    <div className="flex gap-2">
                      {r.fineCreated && (
                        <StatusBadge variant="warning">
                          Fine: ₦{parseFloat(r.fineCreated.amount).toFixed(2)}
                        </StatusBadge>
                      )}
                      {r.holdFulfilled && <StatusBadge variant="info">Hold Fulfilled</StatusBadge>}
                      {!r.fineCreated && !r.holdFulfilled && (
                        <StatusBadge variant="success">OK</StatusBadge>
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
          )}

          {!lastCheckIn && checkInHistory.length === 0 && (
            <EmptyState
              icon={<RotateCcw className="w-6 h-6 text-slate-400" />}
              title="No check-ins yet"
              description="Scan an item barcode above to check it in"
            />
          )}
        </TabsContent>
      </Tabs>

      {/* ═══════════════════════════════════════════════════════ */}
      {/*  DIALOGS                                                */}
      {/* ═══════════════════════════════════════════════════════ */}

      {/* Override dialog */}
      <Dialog
        open={overrideOpen}
        onOpenChange={(open) => {
          if (!open && checkoutLoading) return;
          setOverrideOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
              </div>
              Override Required
            </DialogTitle>
            <DialogDescription>
              This checkout is blocked. Enter a reason to override.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="overrideReason">Reason for override</Label>
              <Input
                id="overrideReason"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Enter reason for override..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleOverrideConfirm();
                }}
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => setOverrideOpen(false)}
                disabled={checkoutLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleOverrideConfirm}
                disabled={!overrideReason.trim() || checkoutLoading}
                className="gap-2"
              >
                {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Override & Check Out
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Declare Lost dialog (FR-24) */}
      <Dialog open={lostDialogOpen} onOpenChange={setLostDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50">
                <Ban className="w-4 h-4 text-red-500" />
              </div>
              Declare Item Lost
            </DialogTitle>
            <DialogDescription>
              Mark this item as lost. A replacement fine will be charged to the patron.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="lostCost">Replacement Cost (₦)</Label>
              <Input
                id="lostCost"
                type="number"
                min="0"
                step="0.01"
                value={lostReplacementCost}
                onChange={(e) => setLostReplacementCost(e.target.value)}
                placeholder="Leave blank to use policy max fine"
              />
              <p className="text-xs text-slate-500">
                Optional — defaults to the policy maximum fine if left blank
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="lostNotes">Notes</Label>
              <Textarea
                id="lostNotes"
                value={lostNotes}
                onChange={(e) => setLostNotes(e.target.value)}
                placeholder="Additional details..."
                rows={2}
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setLostDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeclareLost} disabled={lostLoading}>
                {lostLoading ? <LoadingSpinner /> : "Confirm Lost"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Declare Damaged dialog (FR-25) */}
      <Dialog open={damagedDialogOpen} onOpenChange={setDamagedDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50">
                <ShieldAlert className="w-4 h-4 text-amber-500" />
              </div>
              Declare Item Damaged
            </DialogTitle>
            <DialogDescription>Assess damage and apply a fine to the patron.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="dmgAmount">
                Fine Amount (₦) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="dmgAmount"
                type="number"
                min="0"
                step="0.01"
                value={damagedFineAmount}
                onChange={(e) => setDamagedFineAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dmgDesc">
                Damage Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="dmgDesc"
                value={damagedDescription}
                onChange={(e) => setDamagedDescription(e.target.value)}
                placeholder="Describe the damage (e.g. torn pages, water damage)..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dmgNotes">Notes</Label>
              <Textarea
                id="dmgNotes"
                value={damagedNotes}
                onChange={(e) => setDamagedNotes(e.target.value)}
                placeholder="Additional details..."
                rows={2}
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setDamagedDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleDeclareDamaged}
                disabled={damagedLoading || !damagedFineAmount || !damagedDescription.trim()}
              >
                {damagedLoading ? <LoadingSpinner /> : "Confirm Damaged"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default CirculationDeskPage;
